<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid';
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup';
import { showError } from '@botto/core';
import { fetchMessageDetailByCorrelation, type MessageLogRow } from '../../api';

const props = defineProps<{
  correlationId?: string | number | null;
}>();

const rows = ref<MessageLogRow[]>([]);
const loading = ref(false);
const popupOpen = ref(false);
const popupRow = ref<MessageLogRow | null>(null);

const correlation = computed(() => {
  const id = props.correlationId;
  if (id == null) return '';
  return String(id).trim();
});

const popupTitle = computed(() => {
  const row = popupRow.value;
  if (!row) return 'Сообщение';
  const id = row.correlation_id ?? row.message_id ?? row.id;
  return id != null && id !== '' ? `Сообщение ${id}` : 'Сообщение';
});

const popupFields = computed(() => {
  const row = popupRow.value;
  if (!row) return [];
  return Object.keys(row)
    .filter((key) => !key.startsWith('__'))
    .sort((a, b) => a.localeCompare(b))
    .map((key) => ({ key, value: formatFieldValue(row[key]) }));
});

function formatFieldValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

async function loadHistory() {
  const id = correlation.value;
  if (!id) {
    rows.value = [];
    return;
  }
  loading.value = true;
  try {
    rows.value = await fetchMessageDetailByCorrelation(id);
  } catch (e) {
    showError(e);
    rows.value = [];
  } finally {
    loading.value = false;
  }
}

function openPopup(e: { data?: MessageLogRow }) {
  if (!e.data) return;
  popupRow.value = e.data;
  popupOpen.value = true;
}

function closePopup() {
  popupOpen.value = false;
}

watch(
  correlation,
  () => {
    void loadHistory();
  },
  { immediate: true },
);
</script>

<template>
  <div class="message-detail-history">
    <DxDataGrid
      class="message-detail-history__grid"
      :data-source="rows"
      :show-borders="true"
      :row-alternation-enabled="true"
      :column-auto-width="true"
      :hover-state-enabled="true"
      :load-panel="{ enabled: true }"
      :disabled="loading"
      key-expr="__key"
      no-data-text="Нет записей истории по этому correlation_id."
      @row-click="openPopup"
    >
      <DxColumn data-field="created_at" caption="Дата" />
      <DxColumn data-field="correlation_id" caption="correlation_id" />
      <DxColumn data-field="type" caption="type" />
      <DxColumn data-field="status" caption="status" />
      <DxColumn data-field="phone_from" caption="Отправитель" />
      <DxColumn data-field="phone_to" caption="Получатель" />
    </DxDataGrid>

    <DxPopup
      v-model:visible="popupOpen"
      :title="popupTitle"
      :width="720"
      :height="560"
      :show-close-button="true"
      :hide-on-outside-click="true"
    >
      <template #content>
        <div v-if="popupRow" class="detail-popup">
          <table class="detail-popup__table">
            <tbody>
              <tr v-for="field in popupFields" :key="field.key">
                <th>{{ field.key }}</th>
                <td><pre>{{ field.value }}</pre></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <DxToolbarItem
        widget="dxButton"
        toolbar="bottom"
        location="after"
        :options="{ text: 'Закрыть', onClick: closePopup }"
      />
    </DxPopup>
  </div>
</template>

<style scoped>
.message-detail-history {
  padding: 8px 4px 12px;
}

.message-detail-history__grid :deep(.dx-data-row) {
  cursor: pointer;
}

.detail-popup {
  height: 100%;
  overflow: auto;
}

.detail-popup__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.detail-popup__table th {
  width: 32%;
  text-align: left;
  vertical-align: top;
  padding: 6px 12px 6px 0;
  color: var(--botto-color-text-muted, #5a6478);
  font-weight: 500;
  white-space: nowrap;
}

.detail-popup__table td {
  padding: 6px 0;
  color: var(--botto-color-text, #15203d);
  word-break: break-word;
}

.detail-popup__table pre {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
